import { useState } from 'react'
import { useForm, type UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { ThinXIcon } from 'src/shared/components/icons/toasts/thin-x-icon'
import { BottomSheet } from 'src/shared/components/ui/bottom-sheet'
import { WebpImage } from 'src/shared/components/ui/image/webp-image'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/shared/components/ui/popups/dialog'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'

import { useCreateSupportTicket } from '../support/api/use-create-support-ticket'

import ListingMapFallback from './assets/listing-map.jpg'
import ListingMapWebp from './assets/listing-map.webp'

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type FormValues = z.infer<typeof formSchema>

interface ListingAlertPopupProps {
  open: boolean
  setOpen: (open: boolean) => void
}

type FormSubmitHandler = ReturnType<UseFormReturn<FormValues>['handleSubmit']>

interface ListingAlertPopupUIProps extends ListingAlertPopupProps {
  form: UseFormReturn<FormValues>
  onSubmit: FormSubmitHandler
  isSubmitting: boolean
  showSuccessView: boolean
}

export default function ListingAlertPopup({ open, setOpen }: ListingAlertPopupProps) {
  const { isMobile } = useBreakpoint()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessView, setShowSuccessView] = useState(false)
  const { toast } = useToast()
  const createSupportTicket = useCreateSupportTicket()

  const form = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  })

  const submitEmail = async (data: FormValues) => {
    if (isSubmitting) return

    try {
      formSchema.parse(data)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)
    try {
      await createSupportTicket.mutateAsync({
        email: data.email,
        type: 'other_inquiries',
        description: 'I want to be notified when home search is available on Conrad, my email is ' + data.email,
      })
      form.reset()
      setShowSuccessView(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDesktopSubmit = form.handleSubmit((data) => submitEmail(data))
  const handleMobileSubmit = form.handleSubmit((data) => submitEmail(data))

  if (isMobile) {
    return (
      <ListingAlertPopupMobile
        open={open}
        setOpen={setOpen}
        form={form}
        onSubmit={handleMobileSubmit}
        isSubmitting={isSubmitting}
        showSuccessView={showSuccessView}
      />
    )
  }

  return (
    <ListingAlertPopupDesktop
      open={open}
      setOpen={setOpen}
      form={form}
      onSubmit={handleDesktopSubmit}
      isSubmitting={isSubmitting}
      showSuccessView={showSuccessView}
    />
  )
}

function ListingAlertPopupDesktop({
  open,
  setOpen,
}: // form,
// onSubmit,
// isSubmitting,
// showSuccessView,
ListingAlertPopupUIProps) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent hideClose={true} className="w-full max-w-none border-0 bg-transparent p-0 shadow-none">
        <div className="flex w-full items-center justify-center px-5 py-6">
          <div className="mx-auto flex w-full max-w-[1000px] items-center justify-center">
            <div className="relative flex min-h-[430px] items-center justify-center rounded-[40px] bg-background">
              {/* Close button */}
              <button
                type="button"
                aria-label="Close listing alert"
                onClick={() => setOpen(false)}
                className="absolute right-5 top-5 flex size-[40px] items-center justify-center rounded-full bg-primary text-white xs:right-[29px] xs:top-[29px]"
              >
                <ThinXIcon size={14} className="text-white" />
              </button>

              {/* Content */}
              <div className="flex h-full flex-col items-center gap-14 px-8 pb-10 pt-[60px] lg:flex-row lg:py-4 lg:pl-12 lg:pr-2">
                <div className="flex max-w-[433px] flex-col">
                  <p className="text-[16px] font-medium uppercase tracking-[0.48px] text-secondary">Coming soon</p>
                  <DialogHeader className="mt-[6px] items-start space-y-0 text-left">
                    <DialogTitle className="text-[36px] font-medium leading-[1.4] tracking-[-0.72px] text-primary">
                      {'The future of home search is here'}
                    </DialogTitle>
                  </DialogHeader>
                  <p className="mt-[14px] text-[18px] font-normal leading-[1.55] text-muted">
                    {
                      'Conrad links the right people and the right steps, so your search turns into a plan, not a pile of tabs.'
                    }
                  </p>
                  {/* <Form {...form}>
                    <form onSubmit={onSubmit}>
                      <div className="mt-8 flex h-[70px] w-full max-w-[420px] items-center gap-3 rounded-full bg-accent pl-2 pr-2.5">
                        {showSuccessView ? (
                          <p className="pl-4 text-base text-muted">{'You’ll get notified, stay tuned!'}</p>
                        ) : (
                          <>
                            <AuthInputFormField
                              control={form.control}
                              name="email"
                              label="What’s your email?"
                              type="text"
                            />
                            <Button
                              type="submit"
                              className="h-[50px] shrink-0 rounded-full bg-primary px-5 py-3 text-[15px] font-medium leading-[1.6] text-white shadow-[0_0_1px_0_rgba(170,171,173,0.15)]"
                              isLoading={isSubmitting}
                            >
                              Get notified
                            </Button>
                          </>
                        )}
                      </div>
                    </form>
                  </Form> */}
                </div>

                <div className="flex w-full items-center justify-center">
                  <WebpImage
                    src={ListingMapWebp}
                    fallbackSrc={ListingMapFallback}
                    alt="Listing map preview"
                    containerClassName="h-[400px] w-full max-w-[450px] overflow-hidden rounded-[30px]"
                    className="rounded-[30px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ListingAlertPopupMobile({
  open,
  setOpen,
}: // form,
// onSubmit,
// isSubmitting,
// showSuccessView,
ListingAlertPopupUIProps) {
  return (
    <BottomSheet
      open={open}
      close={() => setOpen(false)}
      showCloseButton={false}
      backdropOpacity={0.4}
      maxHeight="calc(100dvh - 80px)"
      className="rounded-t-[40px]"
    >
      <div className="mb-10">
        <div className="relative mx-auto w-full max-w-[340px]">
          <button
            type="button"
            aria-label="Close listing alert"
            onClick={() => setOpen(false)}
            className="absolute right-0 top-[12px] flex size-[40px] items-center justify-center text-primary"
          >
            <ThinXIcon size={14} className="text-primary" />
          </button>

          <div className="flex flex-col items-center gap-4 pt-9 text-center">
            <div className="flex w-full flex-col items-center gap-[6px]">
              <p className="text-[14px] font-medium uppercase tracking-[0.42px] text-secondary">Coming soon</p>
              <p className="max-w-[250px] text-[26px] font-medium leading-[1.4] tracking-[-0.52px] text-primary">
                {'The future of home search is here'}
              </p>
            </div>
            <p className="text-[15px] font-normal leading-[1.55] text-muted">
              {
                'Conrad links the right people and the right steps, so your search turns into a plan, not a pile of tabs.'
              }
            </p>
          </div>

          {/* <Form {...form}>
            <form onSubmit={onSubmit} className="mt-6">
              <div className="flex h-[60px] items-center gap-3 rounded-full bg-accent py-3.5 pl-5 pr-2.5">
                {showSuccessView ? (
                  <p className="pl-1 text-base text-muted">{'You’ll get notified, stay tuned!'}</p>
                ) : (
                  <>
                    <input
                      type="email"
                      aria-label="Email address"
                      placeholder="What’s your email?"
                      {...form.register('email')}
                      className="min-w-0 flex-1 bg-transparent text-[16px] font-medium leading-none text-primary placeholder:text-muted focus-visible:outline-none"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      className="size-[40px] rounded-full bg-primary text-white shadow-[0_0_1px_0_rgba(170,171,173,0.15)]"
                      isLoading={isSubmitting}
                    >
                      <ArrowRight size={20} strokeWidth={2} className="text-white" />
                    </Button>
                  </>
                )}
              </div>
            </form>
          </Form> */}

          <div className="mt-[47px]">
            <WebpImage
              src={ListingMapWebp}
              fallbackSrc={ListingMapFallback}
              alt="Listing map preview"
              containerClassName="h-[301px] w-full overflow-hidden rounded-[30px]"
              className="rounded-[30px]"
            />
          </div>
        </div>
      </div>
    </BottomSheet>
  )
}
